import React, { useState } from 'react'
import { Home } from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { CardBody, Card, CardTitle } from 'reactstrap'
import { useFormik } from 'formik'
import styled from 'styled-components'
import { toast } from 'react-toastify'

import SocialMediaForm from './EditInformations/SocialMediaForm'

import api from '~/services/api'
import Breadcrumb from '~/components/Breadcrumb'
import { Row, Col, Show } from '~/components/commons'
import { queries } from '~/constants/mediaQuery'
import { Container } from './styles'
import FilterPanel from './commons/FilterPanel'
import ScheduleSubmitForm from './commons/ScheduleSubmitForm'
import { Alert } from '~/components/Alert'
import PreviewSocialMedia from './Preview/PreviewSocialMedia'

function formatUpdateMask(string) {
  const updateMaskArray = string.split(',')

  const newArray = updateMaskArray.map(item => {
    if (item.startsWith('categories')) {
      return 'categories'
    }
    if (item.startsWith('phoneNumbers')) {
      return 'phoneNumbers'
    }
    return item
  })

  const newString = _.uniq(newArray).toString()

  return newString
}

function flatten(obj, path = []) {
  return !_.isObject(obj) || _.isArray(obj)
    ? { [path.join('.')]: obj }
    : _.reduce(
        obj,
        (cum, next, key) => _.merge(cum, flatten(next, [...path, key])),
        {}
      )
}

function deepPickBy(obj) {
  const pick = object =>
    _.pickBy(object, item => {
      return !!item && !_.isEmpty(item)
    })

  const res = Object.entries(obj).reduce((acc, [key, value]) => {
    if (_.isObject(value) && !_.isArray(value)) {
      const parsed = pick(value)

      if (Object.keys(parsed)?.length) {
        acc[key] = parsed
      }
    } else if (
      (_.isArray(value) && !!value.length) ||
      (!_.isArray(value) && !!value)
    ) {
      acc[key] = value
    }

    return acc
  }, {})

  _.pickBy(obj, item => {
    return !!item && !_.isEmpty(item)
  })

  return res
}

CardContainer.Body = styled(CardBody)`
  padding: 2.5rem;
  overflow-x: auto;
  overflow: visible;

  @media ${queries.less.xl} {
    padding: 1.5rem;
  }
`
function CardContainer({ children, title }) {
  return (
    <Card>
      <CardContainer.Body>
        <Show condition={!!title}>
          <CardTitle>
            <FormattedMessage id={title} />
          </CardTitle>
        </Show>
        {children}
      </CardContainer.Body>
    </Card>
  )
}

function EditSocialMedia() {
  const history = useHistory()
  const intl = useIntl()
  const projectId = useSelector(state => state.user?.selectedProject?.id)

  const [updateFields, setUpdateFields] = useState({})
  const [updateMask, setUpdateMask] = useState([])
  const [schedule, setSchedule] = useState(null)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [valid, setValid] = useState(false)
  const [opened, setOpened] = useState(false)
  const [alertsVisible, setAlertsVisible] = useState({
    error: false,
    updated: false,
    scheduled: false,
  })
  const [removeUrls, setRemoveUrls] = useState([])
  const [locationNames, setLocationNames] = useState([])

  const informationsFormik = useFormik({
    initialValues: {
      'attributes/url_facebook': [],
      'attributes/url_instagram': [],
      'attributes/url_linkedin': [],
      'attributes/url_pinterest': [],
      'attributes/url_tiktok': [],
      'attributes/url_twitter': [],
      'attributes/url_youtube': [],
      scheduleEdition: '',
    },
  })

  async function handleSubmit() {
    setError('')
    setSubmitting(true)

    const body = {
      attributes: _.omit(updateFields, ['scheduleEdition']),
      locationIds: locationNames,
      projectId,
    }

    const params = {
      updateMask,
    }

    if (schedule) {
      params.schedule = updateFields.scheduleEdition
    }

    try {
      await api.patch(`/locations/social-media`, body, {
        params,
      })

      setAlert(schedule ? 'scheduled' : 'updated', true)
      setSubmitting(false)
      return true
    } catch (err) {
      setAlert('error', true)
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Your locations NOT updated!'
      )
      setSubmitting(false)
      return false
    }
  }

  async function handleValidateSubmit() {
    const handleError = msg => {
      setValid(false)
      setError(msg)
      setAlert('error', true)
      return false
    }

    setError('')

    const values = deepPickBy(informationsFormik.values)

    if (_.isEmpty(values) && !removeUrls.length) {
      return handleError('You must edit something!')
    }

    if (!values?.locationNames?.length) {
      return informationsFormik.setErrors({
        locationNames: 'You must select some location to update',
      })
    }

    if (values.scheduleEdition) setSchedule(true)

    setLocationNames(values.locationNames)
    delete values.locationNames

    const updateMask = [...Object.keys(flatten(values)), ...removeUrls]
      .filter(k => k !== 'scheduleEdition')
      .join(',')

    setOpened(true)

    try {
      setValid(true)
      setUpdateFields(values)
      setUpdateMask(formatUpdateMask(updateMask))
      toast.success(
        intl.formatMessage({ id: 'The information to be updated is valid!' })
      )
      return true
    } catch (err) {
      toast.success(
        intl.formatMessage({ id: 'The information to be updated is invalid!' })
      )
      return handleError(
        err?.response?.data?.error ||
          err?.message ||
          'Your locations were NOT updated!'
      )
    }
  }

  function gotoHome() {
    history.push('/')
  }

  function setAlert(name, value) {
    setAlertsVisible(old => ({
      ...old,
      [name]: value,
    }))
  }

  const toggle = () => {
    if (submitting) return
    setOpened(prev => !prev)
  }

  return (
    <>
      <Container>
        <Breadcrumb
          paths={[
            {
              render: () => (
                <Link className="d-flex align-items-center" to="/">
                  <Home size={20} />
                </Link>
              ),
            },
            {
              render: () => (
                <strong className="text-primary font-medium-3">
                  <FormattedMessage id="Informations" />
                </strong>
              ),
            },
            {
              render: () => (
                <span className="font-medium-3">
                  <FormattedMessage id="Social Media" />
                </span>
              ),
            },
          ]}
        />

        <Card>
          <Row>
            <Col md={6}>
              <CardContainer>
                <SocialMediaForm
                  formik={informationsFormik}
                  removeUrls={removeUrls}
                  onChangeRemoveURLs={setRemoveUrls}
                />
              </CardContainer>
            </Col>
            <Col md={6}>
              <CardContainer>
                <FilterPanel formik={informationsFormik} />
                <ScheduleSubmitForm
                  formik={informationsFormik}
                  onSubmit={handleValidateSubmit}
                  loading={submitting}
                />
              </CardContainer>
            </Col>
          </Row>
        </Card>
        <PreviewSocialMedia
          toggleModal={toggle}
          isOpen={opened}
          modalData={informationsFormik.values}
          removeUrls={removeUrls}
          onSubmit={() => handleSubmit()}
          isSubmiting={submitting}
          isValid={valid}
          // showHours={showHours}
        />
      </Container>
      <Alert
        type="error"
        visible={alertsVisible.error}
        onClose={() => setAlert('error', false)}
        onCancel={() => setAlert('error', false)}
        confirmBtnText="Try again"
        body={error}
        title="Error"
        closeOnClickOutside
      />
      <Alert
        type="success"
        visible={alertsVisible.scheduled}
        onClose={gotoHome}
        confirmBtnText="Go to dashboard"
        body="Your edits have been scheduled!"
        title="Success"
      />
      <Alert
        type="success"
        visible={alertsVisible.updated}
        onClose={gotoHome}
        confirmBtnText="Go to dashboard"
        body="Your locations are updating!"
        title="Success"
      />
    </>
  )
}

export default EditSocialMedia
